import React, { useState } from 'react'
import type { CalendarEvent, OptionsDocument } from 'paintscout'
import moment from 'moment-timezone'
import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { EstimatorAvatar, EventPreviewPopper, useClientOptions, useDialogs, useUsers } from '@paintscout/ui-business'
import type { User } from '@paintscout/api'
import { useCancelEventFlow } from 'src/hooks'
import history from 'shared/util/routing/history'
import { formatName, getTimezone, translateEventType } from '@paintscout/util/builder'
import type { CrewPalette } from '../../ProductionCalendar/util'
import { getCrewPalette } from '../../ProductionCalendar/util'
import { EventDialog } from '../../../dialogs'
import { useCalendar } from '../CalendarContext'
import { GOOGLE_CALENDAR_EVENT_TYPE } from '../calendarUtils'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
    borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  },
  dateLabel: {
    flexShrink: 0,
    color: '#354962',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '20px'
  },
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    minWidth: '28px',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
    color: theme.paintscout.palette.brand.Neutral.P60,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 500,
    lineHeight: 1,
    textAlign: 'center'
  },
  events: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 1),
    overflowY: 'auto',
    flex: 1,
    minHeight: 0
  },
  noEvents: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(5, 6, 3, 6),
    gap: theme.spacing(2),
    color: theme.paintscout.palette.brand.Neutral.P90
  }
}))

const useCardStyles = makeStyles<Theme, CrewPalette>((theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1, 1.5),
    paddingLeft: theme.spacing(2.5),
    borderRadius: theme.borderRadius.md,
    backgroundColor: ({ surface }) => surface,
    cursor: 'pointer',
    minHeight: '60px',
    transition: 'background-color 0.1s ease-in-out',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: ({ surfaceHover }) => surfaceHover
      }
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: theme.spacing(1),
      top: theme.spacing(1),
      bottom: theme.spacing(1),
      width: '4px',
      borderRadius: '999px',
      backgroundColor: ({ accent }) => accent
    }
  },
  textColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    flex: 1
  },
  title: {
    color: ({ accent }) => accent,
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  time: {
    color: ({ accentSoft }) => accentSoft,
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  avatar: {
    flexShrink: 0
  }
}))

interface SalesMonthEventCardProps {
  event: CalendarEvent
  users: User[]
  userPictures: Record<string, string>
  options: OptionsDocument
  onClick: (el: HTMLElement) => void
}

function SalesMonthEventCard({ event, users, userPictures, options, onClick }: SalesMonthEventCardProps) {
  const palette = getCrewPalette(event.estimator?.color)
  const classes = useCardStyles(palette)
  const estimatorUser = users?.find((u) => u.user_id === event.estimator?.id)
  const timezone = getTimezone({ options })

  const isGoogle = event.eventType === GOOGLE_CALENDAR_EVENT_TYPE
  const title = isGoogle
    ? event.title || 'Event'
    : event.contact
      ? formatName(event.contact)
      : translateEventType({ eventType: event.eventType, options })

  const startM = moment(event.start).tz(timezone)
  const endM = moment(event.end).tz(timezone)
  const timeLabel = event.allDay
    ? 'All day'
    : startM.isSame(endM)
      ? startM.format('h:mma')
      : `${startM.format('h:mma')} - ${endM.format('h:mma')}`

  const eventTypeLabel = !isGoogle && event.contact ? translateEventType({ eventType: event.eventType, options }) : null
  const timeAndType = eventTypeLabel ? `${timeLabel} • ${eventTypeLabel}` : timeLabel

  return (
    <div className={classes.root} onClick={(e) => onClick(e.currentTarget as HTMLElement)} role="button" tabIndex={0}>
      <div className={classes.textColumn}>
        <Typography component="div" className={classes.title}>
          {title}
        </Typography>
        <Typography component="div" className={classes.time}>
          {timeAndType}
        </Typography>
      </div>
      <div className={classes.avatar}>
        <EstimatorAvatar
          userPictures={userPictures}
          user={{
            id: event.estimator?.id,
            firstName: estimatorUser?.user_metadata?.firstName,
            lastName: estimatorUser?.user_metadata?.lastName
          }}
          borderColor={event.estimator?.color}
        />
      </div>
    </div>
  )
}

interface MobileEventTableProps {
  events: CalendarEvent[]
}

export default function MobileEventTable({ events }: MobileEventTableProps) {
  const classes = useStyles()
  const { options } = useClientOptions()
  const { date, updateEvent, deleteEvent } = useCalendar()
  const { users, userPictures } = useUsers()
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { openDialog, dismissDialog } = useDialogs()
  const cancelEventFlow = useCancelEventFlow()

  const ordered = [...events].sort((a, b) => moment(a.start).diff(moment(b.start)))

  const handleEditEvent = () => {
    openDialog(EventDialog, {
      event: selectedEvent,
      onDelete: (event: CalendarEvent, eventTemplate: string) => {
        deleteEvent(event, eventTemplate)
      },
      onConfirm: (event: CalendarEvent, eventTemplate: string) => {
        updateEvent(event, eventTemplate)
        dismissDialog()
      }
    })
    setAnchorEl(null)
    setSelectedEvent(null)
  }

  const handleCancelEvent = (event: CalendarEvent) =>
    cancelEventFlow(event, (evt, template) => {
      deleteEvent(evt, template)
      setAnchorEl(null)
    })

  const handleCloseDrawer = () => {
    setAnchorEl(null)
    setTimeout(() => {
      setSelectedEvent(null)
    }, 300)
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="subtitle2" className={classes.dateLabel}>
          {moment(date).format('dddd, MMMM D')}
        </Typography>
        {ordered.length > 0 && <span className={classes.countBadge}>{ordered.length}</span>}
      </div>
      {ordered.length > 0 ? (
        <div className={classes.events}>
          {ordered.map((event) => (
            <SalesMonthEventCard
              key={event.id}
              event={event}
              users={users}
              userPictures={userPictures}
              options={options}
              onClick={(el) => {
                setSelectedEvent(event)
                setAnchorEl(el)
              }}
            />
          ))}
        </div>
      ) : (
        <div className={classes.noEvents}>
          <i className="fa-regular fa-calendar fa-2x" />
          <Typography variant="subtitle1" color="textSecondary">
            No events scheduled
          </Typography>
        </div>
      )}
      <EventPreviewPopper
        event={selectedEvent}
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        users={users}
        onClose={handleCloseDrawer}
        onEditEvent={handleEditEvent}
        onCancelEvent={handleCancelEvent}
        onNavigate={(path) => history.push(path)}
      />
    </div>
  )
}
